import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Flame, ChevronRight, Loader2, Calendar } from "lucide-react";
import { fetchActiveSchools, filterSchools } from "@/lib/texasSchools";
import { validateRegistration } from "@/lib/validation";
import { fetchActiveEvent } from "@/lib/events";
import { api } from "@/lib/api";
import { useStudentSession } from "@/hooks/useStudentSession";
import type { School, Event } from "@/types/database";

interface Props {
  onRegistered: () => void;
}

export default function StudentRegister({ onRegistered }: Props) {
  const { recordRegistration } = useStudentSession();
  const [schools, setSchools] = useState<School[]>([]);
  const [activeEvent, setActiveEvent] = useState<Event | null>(null);
  const [search, setSearch] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [schoolId, setSchoolId] = useState<string | undefined>(undefined);
  const [newSchoolName, setNewSchoolName] = useState("");
  const [newSchoolCity, setNewSchoolCity] = useState("");
  const [showAddSchool, setShowAddSchool] = useState(false);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    void fetchActiveSchools().then(setSchools).catch(() => setSchools([]));
    void fetchActiveEvent().then(setActiveEvent).catch(() => setActiveEvent(null));
  }, []);

  const filtered = useMemo(() => filterSchools(schools, search), [schools, search]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setServerError(null);
    const input = {
      full_name: fullName.trim(),
      email: email.trim(),
      school_id: schoolId,
      school_name: showAddSchool ? newSchoolName.trim() : undefined,
      city: showAddSchool ? newSchoolCity.trim() : undefined,
      consent_acknowledged: consent,
    };
    const v = validateRegistration(input);
    if (!v.ok) { setErrors(v.errors as Record<string, string>); return; }
    setErrors({});
    setSubmitting(true);
    try {
      const result = await api.registerStudent(input);
      const chosenSchool = schools.find((s) => s.id === result.school_id) ?? null;
      recordRegistration(
        {
          full_name: input.full_name,
          email: input.email,
          school_name: chosenSchool?.name ?? input.school_name ?? null,
        },
        result
      );
      onRegistered();
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={submit}
      className="card-dark p-6 sm:p-8 max-w-md w-full mx-auto"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 rounded-xl bg-forge-500/15 border border-forge-500/30">
          <Flame className="w-7 h-7 text-forge-400" />
        </div>
        <div>
          <h1 className="font-display text-2xl text-white leading-tight">FORGED WeldQuest Texas</h1>
          <p className="text-sm text-steel-300">Compete against welding students across Texas.</p>
        </div>
      </div>

      {activeEvent && (
        <div className="mb-5 rounded-xl bg-forge-500/10 border border-forge-500/30 px-4 py-3 flex items-start gap-2">
          <Calendar className="w-5 h-5 text-forge-400 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="text-forge-200 uppercase tracking-wider text-xs">You are competing in</div>
            <div className="text-white font-medium leading-tight">{activeEvent.name}</div>
            <div className="text-steel-300 text-xs mt-0.5">
              {[
                activeEvent.location,
                activeEvent.city,
                activeEvent.starts_at && new Date(activeEvent.starts_at).toLocaleDateString(),
              ].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="full_name">Full name</label>
          <input
            id="full_name"
            className="input"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            placeholder="Jane Welder"
            required
          />
          {errors.full_name && <p className="text-rose-400 text-sm mt-1">{errors.full_name}</p>}
        </div>

        <div>
          <label className="label" htmlFor="email">Email</label>
          <input
            id="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            type="email"
            placeholder="you@school.edu"
            required
          />
          {errors.email && <p className="text-rose-400 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="label">School</label>
          {!showAddSchool ? (
            <>
              <input
                className="input mb-2"
                placeholder="Search Texas welding programs…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search schools"
              />
              <div className="max-h-44 overflow-y-auto rounded-xl border border-steel-700 divide-y divide-steel-700">
                {filtered.length === 0 && (
                  <div className="p-3 text-sm text-steel-300">No matches.</div>
                )}
                {filtered.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSchoolId(s.id)}
                    className={
                      "w-full text-left px-3 py-2 hover:bg-steel-800 " +
                      (schoolId === s.id ? "bg-forge-500/15 text-forge-200" : "")
                    }
                  >
                    <div className="font-medium">{s.name}</div>
                    <div className="text-xs text-steel-300">
                      {s.city}{s.county ? `, ${s.county} County` : ""}
                    </div>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => { setShowAddSchool(true); setSchoolId(undefined); }}
                className="mt-2 text-forge-300 text-sm hover:underline"
              >
                + My school isn't listed
              </button>
            </>
          ) : (
            <div className="space-y-2">
              <input
                className="input"
                placeholder="School / program name"
                value={newSchoolName}
                onChange={(e) => setNewSchoolName(e.target.value)}
              />
              <input
                className="input"
                placeholder="City (e.g. Houston, Dallas, Alvin)"
                value={newSchoolCity}
                onChange={(e) => setNewSchoolCity(e.target.value)}
              />
              <button
                type="button"
                onClick={() => { setShowAddSchool(false); setNewSchoolName(""); setNewSchoolCity(""); }}
                className="text-steel-300 text-sm hover:underline"
              >
                ← Pick from list
              </button>
              <p className="text-xs text-steel-400">Submitted as pending — manager confirms.</p>
            </div>
          )}
          {errors.school && <p className="text-rose-400 text-sm mt-1">{errors.school}</p>}
          {errors.city && <p className="text-rose-400 text-sm mt-1">{errors.city}</p>}
        </div>

        <label className="flex items-start gap-2 text-sm text-steel-200">
          <input
            type="checkbox"
            className="mt-1 accent-forge-500"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
          />
          I understand my name, school, score, and rank may appear on the leaderboard.
        </label>
        {errors.consent && <p className="text-rose-400 text-sm">{errors.consent}</p>}

        {serverError && <div className="pill-bad px-3 py-2">{serverError}</div>}

        <button type="submit" className="btn-primary w-full text-lg" disabled={submitting}>
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronRight className="w-5 h-5" />}
          {submitting ? "Starting…" : "Start"}
        </button>
        <p className="text-center text-xs text-steel-400">
          Correct answers matter. Speed breaks the tie.
        </p>
      </div>
    </motion.form>
  );
}
